
import { computed, defineComponent, onMounted, PropType } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import 'dayjs/locale/ja'
dayjs.locale('ja')
dayjs.extend(isBetween)
import ScrollHint from 'scroll-hint'
import '../../../node_modules/scroll-hint/css/scroll-hint.css'
import { isTouchDevice } from '@/utils/commonMethods'
import { isNowBetweenTwoTimeString } from '@/utils/eventDetailOnlineTableUtils'
import EventDetailOnlineTableCellLabels from './EventDetailOnlineTableCellLabels.vue'

import {
  EventDetailOnlineChannel,
  EventDetailOnlineProgram
} from '@/types/EventDetail'

export default defineComponent({
  name: 'EventDetailOnlineTable',
  components: {
    EventDetailOnlineTableCellLabels
  },
  props: {
    type: String,
    eventDay: {
      type: String, // YYYY-MM-DD HH:mm:ss
      default: ''
    },
    currentTime: {
      type: String, // YYYY-MM-DD HH:mm
      default: ''
    },
    channels: {
      type: Array as PropType<EventDetailOnlineChannel[]>,
      default: () => []
    },
    hospitals: {
      type: Object,
      default: () => ({})
    },
    viewLive: {
      type: Function as PropType<
        (
          channelName: string,
          selectedProgramStart: string,
          btnType?: string
        ) => void
      >,
      required: true
    }
  },
  setup(props) {
    const scrollAnime = onMounted(() => {
      new ScrollHint('.eventOnlineTable', {
        i18n: {
          scrollable: '横スクロールで表示'
        }
      } as any)
    })

    // NOTE: ヘッダーを構成するためtimeHeaderItems: TimeHeaderItem[]を用意して、
    // 事前にtimeとnowOnAirの情報を詰める→高階関数をやめる
    const nowOnAir = computed(() =>
      isNowBetweenTwoTimeString(props.currentTime, props.eventDay)
    )

    const timeArray = computed(() => {
      if (!props.channels.length) return []
      const array: string[] = []
      props.channels.forEach((channel) => {
        if (!channel.programs.length) return
        channel.programs.forEach((program) => {
          if (
            !array.includes(`${program.start_hour}:${program.start_minute}`)
          ) {
            array.push(`${program.start_hour}:${program.start_minute}`)
          }
        })
      })
      array.sort((a: string, b: string) => {
        return Number(a.replace(':', '')) - Number(b.replace(':', ''))
      })
      return array
    })

    const endTimeArray = computed(() => {
      if (!props.channels.length) return []
      const array: string[] = []
      props.channels.forEach((channel) => {
        if (!channel.programs.length) return
        channel.programs.forEach((program) => {
          if (!array.includes(`${program.end_hour}:${program.end_minute}`)) {
            array.push(`${program.end_hour}:${program.end_minute}`)
          }
        })
      })
      array.sort((a: string, b: string) => {
        return Number(a.replace(':', '')) - Number(b.replace(':', ''))
      })
      return array
    })

    const editedChannels = computed(() => {
      if (!props.channels.length || !props.eventDay) return []

      const res: EventDetailOnlineChannel[] = []
      props.channels.forEach((channel) => {
        const temp: EventDetailOnlineChannel = {
          channel_name: channel.channel_name,
          channel_description: channel.channel_description,
          programs: []
        }
        // 番組の終了状態を把握する関数
        if (channel.programs && channel.programs.length) {
          temp.programs = channel.programs.map((program) => {
            const now = dayjs(props.currentTime)
            const eventYYYYMMDD = props.eventDay.split(' ')[0]
            const pEnd = dayjs(
              `${eventYYYYMMDD} ${program.end_hour}:${program.end_minute}`
            )

            program.isEnable = now.isBefore(pEnd)

            if (!program.hospital_id)
              program.hospital_name = 'マイナビRESIDENT運営事務局'
            if (props.hospitals[program.hospital_id])
              program.hospital_name =
                props.hospitals[program.hospital_id].hospital_name

            return program
          })
        }
        res.push(temp)
      })
      return res
    })

    const tableWidth = (type: string) => {
      const thWidth = type !== 'b' ? 172 : 0
      return {
        width: 210 * timeArray.value.length + thWidth + 'px'
      }
    }

    // 時刻のthセルに各tdセルの縦をあわせるための処理。channel.programsを引数に受け取り、ずらす分だけ空の中身を追加する
    const makeEmptyCell = (
      items: EventDetailOnlineProgram[]
      // ここで空オブジェクトの可能性があるせいでうまくいかん
    ): Array<EventDetailOnlineProgram | {}> => {
      const resultArray: {}[] = []
      // 全プログラムをmapでまわしてstartを取得した配列作る
      const itemStartArray = items.map(
        (x) => `${x.start_hour}:${x.start_minute}`
      )
      // timeArrayをまわしてstartと一致するものがなければ空セルをpush
      timeArray.value.forEach((time: string) => {
        if (itemStartArray.includes(time)) {
          const tartgetIndex = itemStartArray.indexOf(time)
          resultArray.push(items[tartgetIndex])
        } else {
          resultArray.push({})
        }
      })
      return resultArray
    }

    const trimString = (str: string): string => {
      const number = 17
      return str.length > number ? str.substr(0, number) + '…' : str
    }

    onMounted(() => {
      const table = document.querySelector('#js-eventOnlineTable')
      const tableHead = table?.querySelector('thead')
      let tableHeight = 0
      let tablePosition = 0
      let lastTime = 0

      const setTableInfo = () => {
        tableHeight = table ? table.clientHeight : 0
        tablePosition = table
          ? table.getBoundingClientRect().top + window.pageYOffset
          : 0
      }
      setTableInfo()

      const fixTableHeader = () => {
        const now = Date.now()
        if (now - lastTime < 10) return
        lastTime = now

        if (!tablePosition || !tableHeight) return

        if (
          window.pageYOffset >= tablePosition &&
          window.pageYOffset <= tablePosition + tableHeight
        ) {
          if (tableHead) {
            const diff = window.pageYOffset - tablePosition
            tableHead.style.top = diff + 'px'
          }
        } else {
          tableHead?.setAttribute('style', '')
        }
      }

      document.addEventListener('scroll', fixTableHeader)
      window.addEventListener('resize', setTableInfo)

      onBeforeRouteLeave(() => {
        document.removeEventListener('scroll', fixTableHeader)
        window.removeEventListener('resize', setTableInfo)
      })
    })

    return {
      scrollAnime,
      timeArray,
      endTimeArray,
      editedChannels,
      tableWidth,
      makeEmptyCell,
      trimString,
      nowOnAir,
      isTouchDevice
    }
  }
})
